import { useCallback, useEffect, useRef } from 'react';

import { UseMutationResult } from '@tanstack/react-query';
import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconMicrophone } from '@components/icons/microphone';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { useAudioRecorder } from '@hooks/use-audio-recorder';
import { getScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type AudioRecorderButtonProps = {
  onTranscriptionComplete: (text: string) => void;
  onError: (error: string) => void;
  transcriptionMutation: UseMutationResult<{ text: string }, unknown, File, unknown>;
  isDisabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
};

// Minimum audio level threshold to consider input as active
const MIN_ACTIVE_LEVEL = 0.02;
// Duration in ms to continue showing visualizer after audio stops
const TAIL_DURATION_MS = 300;
// Base value for exponential decay of visualizer after audio stops
const DECAY_BASE = 0.85;
// Time interval in ms between decay steps for the visualizer tail
const DECAY_INTERVAL_MS = 50;
// Controls the phase spacing between bars in the wave animation
const WAVELENGTH = 4.0;
// Speed multiplier for the traveling wave animation
const WAVE_SPEED = 2.0;
// Minimum scale factor for visualizer bars (prevents them from disappearing completely)
const MIN_SCALE = 0.15;
// Number of bars in the audio visualizer
const BAR_COUNT = 10;

export const AudioRecorderButton = ({
  onTranscriptionComplete,
  onError,
  transcriptionMutation,
  isDisabled = false,
  size,
}: AudioRecorderButtonProps) => {
  const { t } = getScopedTranslate(locale);
  const { mutateAsync: transcribe, isLoading: isTranscribing } = transcriptionMutation;
  const {
    recordingState,
    audioLevel,
    error: recordingError,
    startRecording,
    stopRecording,
    setOnComplete,
  } = useAudioRecorder();

  const handleRecordingComplete = useCallback(
    async (audioFile: File) => {
      try {
        const result = await transcribe(audioFile);
        onTranscriptionComplete(result.text);
      } catch {
        onError('Failed to transcribe audio.');
      }
    },
    [onError, onTranscriptionComplete, transcribe]
  );

  useEffect(() => {
    setOnComplete(handleRecordingComplete);
  }, [handleRecordingComplete, setOnComplete]);

  useEffect(() => {
    if (recordingError) {
      onError(recordingError);
    }
  }, [onError, recordingError]);

  const handleClick = useCallback(() => {
    if (recordingState === 'recording') {
      stopRecording();
      return;
    }

    if (recordingState === 'idle') {
      startRecording();
    }
  }, [recordingState, startRecording, stopRecording]);

  const isProcessing = recordingState === 'stopping' || isTranscribing;
  const isStarting = recordingState === 'starting';
  const isRecording = recordingState === 'recording';

  const audioLevelRef = useRef(audioLevel);
  const isRecordingRef = useRef(isRecording);
  const smoothedLevelRef = useRef(0);
  const lastActiveTimeRef = useRef(0);
  const lastActiveLevelRef = useRef(0);
  const animationFrameRef = useRef<number>();
  const timeRef = useRef(0);
  const lastFrameTimeRef = useRef(performance.now());
  const isProcessingRef = useRef(isProcessing);
  const barRefsArray = useRef<(HTMLDivElement | null)[]>([]);
  const visualizerContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    audioLevelRef.current = audioLevel;
  }, [audioLevel]);

  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);

  const stopAnimation = useCallback(() => {
    if (animationFrameRef.current !== undefined) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopAnimation();
    };
  }, [stopAnimation]);

  const animateVisualizer = useCallback(() => {
    const step = () => {
      const now = performance.now();
      const level = audioLevelRef.current;

      // Track when audio was last active for tail effect
      if (level > MIN_ACTIVE_LEVEL) {
        lastActiveTimeRef.current = now;
        lastActiveLevelRef.current = level;
      }

      // Calculate exponential decay for visualizer tail after audio stops
      const timeSinceActive = now - lastActiveTimeRef.current;
      const tailActive = timeSinceActive < TAIL_DURATION_MS;
      const decayMultiplier = Math.pow(DECAY_BASE, timeSinceActive / DECAY_INTERVAL_MS);
      const tailLevel = Math.max(0, lastActiveLevelRef.current * decayMultiplier);

      // Use active level or decaying tail level
      const targetLevel = level > MIN_ACTIVE_LEVEL ? level : tailActive ? tailLevel : 0;

      // Smooth audio level changes (60% toward target per frame)
      const nextSmoothedLevel =
        smoothedLevelRef.current + (targetLevel - smoothedLevelRef.current) * 0.6;

      smoothedLevelRef.current = nextSmoothedLevel;

      // Update animation time for traveling wave
      const deltaTime = (now - lastFrameTimeRef.current) / 1000;
      lastFrameTimeRef.current = now;
      timeRef.current += deltaTime;

      // Calculate bar amplitude: clamp level, apply perceptual curve, scale with base offset
      const clampedLevel = Math.max(0, Math.min(1, nextSmoothedLevel));
      const baseAmplitude = isRecordingRef.current ? 0.15 : 0.1;
      const perceivedLevel = Math.pow(clampedLevel, 0.75); // Gamma curve for better visual response
      const amplitude = baseAmplitude + perceivedLevel * 0.85;

      // Update each bar's height using traveling sine wave
      barRefsArray.current.forEach((barElement, index) => {
        if (barElement) {
          const phase = index / WAVELENGTH - timeRef.current * WAVE_SPEED; // Position in wave cycle
          const sine = Math.sin(phase * Math.PI * 2); // -1 to 1
          const scaleY = Math.max(MIN_SCALE, (sine * 0.5 + 0.5) * amplitude); // Normalize to 0-1, then scale
          barElement.style.transform = `scaleY(${scaleY})`;
        }
      });

      // Show/hide visualizer based on activity (hide when processing)
      const shouldShow = isRecordingRef.current && !isProcessingRef.current;
      if (visualizerContainerRef.current) {
        visualizerContainerRef.current.style.display = shouldShow ? 'flex' : 'none';
      }

      const shouldContinue =
        (isRecordingRef.current || nextSmoothedLevel > 0.015 || tailActive) &&
        !isProcessingRef.current;

      if (!shouldContinue) {
        smoothedLevelRef.current = 0;
        timeRef.current = 0;
        if (visualizerContainerRef.current) {
          visualizerContainerRef.current.style.display = 'none';
        }
        stopAnimation();
        return;
      }

      animationFrameRef.current = requestAnimationFrame(step);
    };

    if (animationFrameRef.current === undefined) {
      lastFrameTimeRef.current = performance.now();
      animationFrameRef.current = requestAnimationFrame(step);
    }
  }, [stopAnimation]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
    if (isRecording) {
      timeRef.current = 0;
      animateVisualizer();
    } else if (smoothedLevelRef.current > 0.01) {
      animateVisualizer();
    } else {
      // Stop animation when not recording
      stopAnimation();
    }
  }, [animateVisualizer, isRecording, stopAnimation]);

  return (
    <Stack orientation="horizontal" align="center" space={1}>
      {isRecording && (
        <VisualizerContainer ref={visualizerContainerRef} aria-hidden="true">
          {Array.from({ length: BAR_COUNT }, (_, index) => (
            <VisualizerBar
              key={index}
              ref={(element) => {
                barRefsArray.current[index] = element;
              }}
            />
          ))}
        </VisualizerContainer>
      )}
      {isProcessing || isStarting ? (
        <SpinnerContainer>
          <SizedRelativeSpinner size={16} $skipMargin />
        </SpinnerContainer>
      ) : (
        <IconButton
          icon={<IconMicrophone color={isRecording ? 'elementRed100' : undefined} />}
          onClick={handleClick}
          disabled={isDisabled || isProcessing || isStarting}
          tooltip={isRecording ? t('stopRecording') : t('startRecording')}
          size={size}
        />
      )}
    </Stack>
  );
};

const VisualizerContainer = styled.div({
  height: pxToRem(24),
  width: pxToRem(24),
  marginRight: pxToRem(8),
  display: 'none', // Start hidden, animation will show it
  alignItems: 'center',
  justifyContent: 'center',
  gap: pxToRem(1.5),
  flexShrink: 0,
});

const VisualizerBar = styled.div`
  width: ${pxToRem(2)};
  height: 100%;
  min-height: ${pxToRem(2)};
  background: linear-gradient(
    to bottom,
    ${theme.color.elementPurple60},
    ${theme.color.elementPurple80}
  );
  border-radius: ${pxToRem(1)};
  transform: scaleY(${MIN_SCALE});
  transform-origin: center;
  flex-shrink: 0;
  will-change: transform;
`;

const SpinnerContainer = styled(Stack).attrs({
  align: 'center',
  justify: 'center',
})({
  width: pxToRem(32),
  height: pxToRem(32),
  borderRadius: '50%',
  backgroundColor: theme.color.backgroundLightDark,
});

const locale = {
  en: {
    startRecording: 'Start recording',
    stopRecording: 'Stop recording',
  },
};
